import React, { type CSSProperties, FC, useMemo, useState } from 'react'
import moment from 'moment'
import { JsonEditor } from '@library'
import { type VectorIconLibrary } from '@microservices/wiskey-react-components'
import { ICON_REGISTRY } from '@mobileConstants'
import { useExecutePreviewJs } from '@mobileHooks'
import {
  FieldElement,
  IconElement,
  ItemTemplate,
  MobileListItem,
  RowElement,
  StyleParameters,
  TemplateElement,
  VectorIcon,
  ViewElement,
} from '@mobileTypes'
import { Add as AddIcon, ChevronRight, Code as CodeIcon, Edit } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material'

import { useFetchDisplayRuleByIdQuery } from '@redux/api/displayRules.api'

import { DisplayRule } from '@types'

import { MobilePhoneFrame } from './frames/MobilePhoneFrame'
import { SingleListItemRenderer } from './components/SingleListItemRenderer'
import { VectorIconRenderer } from '@mobileComponents/VectorIconRenderer/VectorIconRenderer'

interface MobileListItemPreviewProps {
  item: Partial<MobileListItem>
  data?: any[]
  isLoading?: boolean
  error?: string | null
  onRefresh?: () => void
  onDataEdit?: (newData: any[]) => void
  commandResultsByKey?: Record<string, any[]>
  commandResultsByObject?: Record<string, any[]>
}

const PREVIEW_MAX_ITEMS = 10

// Constants for styling
const JS_BADGE_COLOR = '#1976d2'
const JS_BADGE_FONT_SIZE = '8px'
const JS_BADGE_MIN_WIDTH = '14px'
const JS_BADGE_HEIGHT = '14px'
const JS_BADGE_PADDING = '0 4px'
const JS_BADGE_BORDER_RADIUS = '7px'
const JS_BADGE_BOX_SHADOW = '0 1px 2px rgba(0,0,0,0.2)'
const CODE_ICON_FONT_SIZE = '16px'
const ICON_DEFAULT_SIZE = 24
const CHEVRON_COLOR = '#5c5c5c'
const CHEVRON_SIZE = 28
const TEXT_SECONDARY_COLOR = '#666'
const BACKGROUND_LIGHT = '#f5f5f5'
const ELEMENT_PADDING = '2px 4px'
const ICON_CONTAINER_PADDING = '8px'
const ICON_CONTAINER_BORDER_RADIUS = '8px'
const DEFAULT_ICON_COLOR = '#000000'

type ExecutePreviewJs = ReturnType<typeof useExecutePreviewJs>

type ComputedElementValue = {
  display: string
  error?: string
  tooltip?: string
  rawValue?: any
}

type BaseScriptContext = {
  appData: any
  appConfig: any
  userData: any
}

enum BORDER_TYPE {
  ELLIPSE = 'ELLIPSE',
  SQUARE = 'SQUARE',
  ROUNDED_SQUARE = 'ROUNDED_SQUARE',
}

type StyleSetting = {
  id: number
  code: string
  internalId: string
  title: string
  background: string
  settingText: {
    id: number
    fontFamily: string
    fontColor: string
    alignment: 'left' | 'center' | 'right'
    fontSize: number
    isBold: boolean
    isItalic: boolean
    version: number | null
    changed: boolean | null
  }
  settingBorder: {
    type: BORDER_TYPE
    color: string
    version: number | null
    changed: boolean | null
  }
  version: number | null
  changed: boolean | null
}

type JsSetting = {
  id: number
  styleSettingScript: string
  styleSettings: StyleSetting[]
  version: number | null
  changed: boolean | null
}

/**
 * Utility function to check if record has data (not empty state)
 */
const hasData = (record: any): boolean => {
  return record && typeof record === 'object' && Object.keys(record).length > 0
}

/**
 * Recursively collects all DisplayRule IDs from a template structure
 */
const collectDisplayRuleIdsFromTemplate = (template: ItemTemplate | undefined): Set<number> => {
  const ids = new Set<number>()
  if (!template?.children) return ids

  const traverse = (elements: TemplateElement[]) => {
    elements.forEach(element => {
      if (element.displayRule?.id) {
        ids.add(element.displayRule.id)
      }
      if (element.type === 'view' && (element as ViewElement).children) {
        traverse((element as ViewElement).children!)
      }
    })
  }

  traverse(template.children)

  return ids
}

const pathToKeys = (path: string): string[] => {
  return path
    .replace(/\[(\w+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)
}

const getValueByPath = (source: any, path?: string) => {
  if (!source || !path) {
    return undefined
  }

  return pathToKeys(path).reduce((acc: any, key) => {
    if (acc === undefined || acc === null) {
      return undefined
    }

    return acc[key]
  }, source)
}

/**
 * Builds a path string from pathArray for display purposes
 */
const buildPathFromPathArray = (pathArray?: Array<{ field?: string | null }> | null): string | undefined => {
  if (!pathArray || pathArray.length === 0) {
    return undefined
  }

  return pathArray
    .map(item => item.field)
    .filter(Boolean)
    .join('.')
}

/**
 * Gets value by pathArray, traversing nested objects
 * pathArray structure: [{ objectCode: string, field: string, valueType: string, ... }]
 */
const getValueByPathArray = (
  source: any,
  pathArray?: Array<{ field?: string | null; objectCode?: string | null }> | null,
  commandResultsByObject?: Record<string, any[]>
): any => {
  if (!source || !pathArray || pathArray.length === 0) {
    return undefined
  }

  let currentValue: any = source

  for (let i = 0; i < pathArray.length; i++) {
    const pathItem = pathArray[i]
    if (!pathItem.field) {
      continue
    }

    // For the first item, if we have objectCode, try to get the linked object from commandResultsByObject
    if (i === 0 && pathItem.objectCode && commandResultsByObject) {
      const objectData = commandResultsByObject[pathItem.objectCode]
      if (objectData && objectData.length > 0) {
        // If currentValue (record) has a reference field value, use it to find the linked object
        if (currentValue && typeof currentValue === 'object') {
          const refFieldValue = currentValue[pathItem.field]
          if (refFieldValue !== undefined && refFieldValue !== null) {
            // Find the linked object in objectData by id or internalId
            const linkedObject = objectData.find(
              (obj: any) => obj.id === refFieldValue || obj.internalId === refFieldValue
            )
            if (linkedObject) {
              currentValue = linkedObject
              continue
            }
          }
        }
      }
    }

    // Traverse the current object by field name
    if (currentValue === undefined || currentValue === null) {
      return undefined
    }

    // Handle both object and array cases
    if (Array.isArray(currentValue)) {
      // If currentValue is an array, try to get the first item
      if (currentValue.length > 0) {
        currentValue = currentValue[0][pathItem.field]
      } else {
        return undefined
      }
    } else if (typeof currentValue === 'object') {
      currentValue = currentValue[pathItem.field]
    } else {
      return undefined
    }
  }

  return currentValue
}

const applyFormatDate = (value: any, formatDate?: string | null) => {
  if (!formatDate) {
    return value
  }

  if (value === undefined || value === null || value === '') {
    return value
  }

  const date = moment(value)

  if (!date.isValid()) {
    return value
  }

  return date.format(formatDate)
}

/**
 * Converts StyleParameters to CSSProperties
 */
const buildStyleFromParameters = (styleParameters?: StyleParameters): CSSProperties => {
  if (!styleParameters) {
    return {}
  }

  const styles: CSSProperties = {}

  if (styleParameters.color) {
    styles.color = styleParameters.color
  }

  if (styleParameters.backgroundColor) {
    styles.backgroundColor = styleParameters.backgroundColor
  }

  if (styleParameters.borderWidth !== undefined && styleParameters.borderWidth !== null) {
    styles.borderWidth =
      typeof styleParameters.borderWidth === 'string'
        ? styleParameters.borderWidth
        : `${styleParameters.borderWidth}px`
    // Add borderStyle if borderWidth is set but borderStyle is not explicitly set
    if (!styles.borderStyle) {
      styles.borderStyle = 'solid'
    }
  }

  if (styleParameters.borderColor) {
    styles.borderColor = styleParameters.borderColor
  }

  if (styleParameters.borderRadius !== undefined && styleParameters.borderRadius !== null) {
    styles.borderRadius =
      typeof styleParameters.borderRadius === 'string'
        ? styleParameters.borderRadius
        : `${styleParameters.borderRadius}px`
  }

  if (styleParameters.padding !== undefined && styleParameters.padding !== null) {
    styles.padding =
      typeof styleParameters.padding === 'string'
        ? styleParameters.padding
        : `${styleParameters.padding}px`
  }

  if (styleParameters.margin !== undefined && styleParameters.margin !== null) {
    styles.margin =
      typeof styleParameters.margin === 'string'
        ? styleParameters.margin
        : `${styleParameters.margin}px`
  }

  if (styleParameters.fontSize !== undefined && styleParameters.fontSize !== null) {
    styles.fontSize =
      typeof styleParameters.fontSize === 'string'
        ? styleParameters.fontSize
        : `${styleParameters.fontSize}px`
  }

  return styles
}

const formatDisplayValue = (value: any): string => {
  if (value === undefined || value === null) {
    return '-'
  }

  if (typeof value === 'string') {
    return value.length > 120 ? `${value.slice(0, 117)}…` : value
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  if (value instanceof Date) {
    return value.toLocaleString()
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return '[]'
    }

    if (value.length > 3) {
      return `${value.slice(0, 3).join(', ')}…`
    }

    return value.join(', ')
  }

  if (typeof value === 'object') {
    const json = JSON.stringify(value)

    return json.length > 140 ? `${json.slice(0, 137)}…` : json
  }

  return String(value)
}

const computeElementValue = (
  element: RowElement,
  record: any,
  recordIndex: number,
  commandResultsByKey: Record<string, any[]>,
  commandResultsByObject: Record<string, any[]>,
  executeJs: ExecutePreviewJs,
  list: any[],
  baseContext: BaseScriptContext
): ComputedElementValue => {
  if (element.bindType === 'js') {
    const { value, error } = executeJs(element.value, {
      ...baseContext,
      ...commandResultsByKey,
      item: record,
      list,
    })
    const tooltip =
      typeof element.value === 'string' && element.value.trim().length ? element.value : undefined

    return {
      display: formatDisplayValue(value),
      error,
      tooltip,
      rawValue: value,
    }
  }

  // If pathArray is provided, use it to get nested value
  if (element.pathArray && element.pathArray.length > 0) {
    const pathArrayValue = getValueByPathArray(record, element.pathArray, commandResultsByObject)

    if (pathArrayValue !== undefined && pathArrayValue !== null && pathArrayValue !== '') {
      const formattedValue = applyFormatDate(pathArrayValue, element.formatDate)

      return {
        display: formatDisplayValue(formattedValue),
        error: undefined,
        rawValue: pathArrayValue,
      }
    }

    // Try fallback to commandResultsByObject if pathArray didn't work
    if (element.objectCode) {
      const fallbackSource = commandResultsByObject[element.objectCode]?.[0]
      if (fallbackSource) {
        const fallbackValue = getValueByPathArray(fallbackSource, element.pathArray, commandResultsByObject)

        if (fallbackValue !== undefined && fallbackValue !== null && fallbackValue !== '') {
          return {
            display: formatDisplayValue(applyFormatDate(fallbackValue, element.formatDate)),
            error: undefined,
            rawValue: fallbackValue,
          }
        }
      }
    }

    // If value not found, return empty display (don't show path or anything)
    return {
      display: '',
      error: undefined,
      rawValue: undefined,
    }
  }

  // Fallback to original logic with element.value
  const baseValue = getValueByPath(record, element.value)

  if (baseValue !== undefined && baseValue !== null && baseValue !== '') {
    const formattedValue = applyFormatDate(baseValue, element.formatDate)

    return {
      display: formatDisplayValue(formattedValue),
      error: undefined,
      rawValue: baseValue,
    }
  }

  if (element.objectCode) {
    const fallbackSource = commandResultsByObject[element.objectCode]?.[0]
    if (fallbackSource) {
      const fallbackValue = getValueByPath(fallbackSource, element.value)

      return {
        display: formatDisplayValue(applyFormatDate(fallbackValue, element.formatDate)),
        error: undefined,
        rawValue: fallbackValue,
      }
    }
  }

  return {
    display: formatDisplayValue(element.value),
    error: undefined,
    tooltip: undefined,
    rawValue: element.value,
  }
}

const renderIcon = (
  icon: MobileListItem['icon'],
  record: any,
  commandResultsByKey: Record<string, any[]>,
  executeJs: ExecutePreviewJs,
  list: any[],
  baseContext: BaseScriptContext,
  displayRulesMap?: Map<number, DisplayRule>
) => {
  if (!icon) {
    return null
  }

  // Check if we have actual data (not empty state) for DisplayRule
  const recordHasData = hasData(record)

  const fullIconDisplayRule =
    icon.displayRule?.id && displayRulesMap ? displayRulesMap.get(icon.displayRule.id) : null
  const rowContext = { ...commandResultsByKey, item: record, list }
  const iconStyles =
    fullIconDisplayRule && recordHasData
      ? generateStyleFromDisplayRule(executeJs, fullIconDisplayRule, rowContext)
      : {}

  const iconContainerStyles: CSSProperties = {
    padding: fullIconDisplayRule ? ICON_CONTAINER_PADDING : undefined,
    borderRadius: fullIconDisplayRule ? ICON_CONTAINER_BORDER_RADIUS : undefined,
    borderWidth: iconStyles.borderWidth ?? 0,
    borderStyle: iconStyles.borderWidth ? 'solid' : undefined,
    borderColor: iconStyles.borderColor || undefined,
    backgroundColor: iconStyles.backgroundColor || undefined,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }


  const iconColor =
    (iconStyles.color as string) || (icon && 'color' in icon ? icon.color : undefined) || DEFAULT_ICON_COLOR

  if (typeof icon === 'string') {
    return (
      <Box sx={iconContainerStyles}>
        <Typography sx={{ fontSize: `${ICON_DEFAULT_SIZE}px` }}>{icon}</Typography>
      </Box>
    )
  }

  if (icon.isJS) {
    // In empty state, show CodeIcon with badge (like for fields)
    if (!recordHasData) {
      const jsCode = typeof icon.value === 'string' ? icon.value : ''
      const tooltip = jsCode.trim().length > 0 ? jsCode : undefined

      return (
        <Box sx={iconContainerStyles}>
          <JSCodeBadge tooltip={tooltip} />
        </Box>
      )
    }

    const { value, error } = executeJs(icon.value, {
      ...baseContext,
      ...commandResultsByKey,
      item: record,
      list,
    })

    if (error) {
      return (
        <Box sx={iconContainerStyles}>
          <Typography sx={{ fontSize: '11px', color: 'error.main' }}>Icon error</Typography>
        </Box>
      )
    }

    if (!value) {
      return null
    }

    if (typeof value === 'string') {
      return (
        <Box sx={iconContainerStyles}>
          <Typography sx={{ fontSize: '11px' }}>{value}</Typography>
        </Box>
      )
    }

    if (typeof value === 'object') {
      const vectorIcon = value as Partial<VectorIcon>
      if (vectorIcon?.type && vectorIcon?.value) {
        const iconForPreview: VectorIcon = {
          value: vectorIcon.value,
          type: vectorIcon.type as VectorIconLibrary,
          size: vectorIcon.size ?? ICON_DEFAULT_SIZE,
          color: (vectorIcon as any).color ?? iconColor,
        }
        const rendered = <VectorIconRenderer icon={iconForPreview} />
        if (rendered) {
          return <Box sx={iconContainerStyles}>{rendered}</Box>
        }
      }

      return (
        <Box sx={iconContainerStyles}>
          <Typography sx={{ fontSize: '11px', color: 'error.main', textAlign: 'center' }}>
            Undefined Icon
          </Typography>
        </Box>
      )
    }

    return (
      <Box sx={iconContainerStyles}>
        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>JS Icon</Typography>
      </Box>
    )
  }

  // Handle static icon: can be VectorIcon object, JSON string with VectorIcon, or string value
  let vectorIcon: VectorIcon | { value: string; size?: number; type?: string } | null = null

  // Try to parse value as JSON (VectorIcon object)
  if (icon.value && typeof icon.value === 'string') {
    try {
      const parsed = JSON.parse(icon.value)
      if (parsed && typeof parsed === 'object' && parsed.type && parsed.value) {
        vectorIcon = parsed as VectorIcon
      } else {
        // Not a JSON object, treat as plain string
        vectorIcon = { value: icon.value, size: icon.size }
      }
    } catch {
      // Not valid JSON, treat as plain string
      vectorIcon = { value: icon.value, size: icon.size }
    }
  } else {
    vectorIcon = icon as VectorIcon
  }

  const iconSize = (vectorIcon as any)?.size ?? ICON_DEFAULT_SIZE

  if (vectorIcon && vectorIcon.value) {
    let iconForPreview: VectorIcon | null = null
    if (vectorIcon.type && vectorIcon.value) {
      iconForPreview = {
        value: vectorIcon.value,
        type: vectorIcon.type as VectorIconLibrary,
        size: iconSize,
        color: (vectorIcon as any).color ?? iconColor,
      }
    }
    if (!iconForPreview && typeof vectorIcon.value === 'string') {
      const found = ICON_REGISTRY.find(i => i.id === vectorIcon!.value)
      if (found) {
        iconForPreview = {
          value: vectorIcon!.value,
          type: found.library,
          size: iconSize,
          color: (vectorIcon as any).color ?? iconColor,
        }
      }
    }
    const rendered = iconForPreview ? <VectorIconRenderer icon={iconForPreview} /> : null
    if (rendered) {
      return <Box sx={iconContainerStyles}>{rendered}</Box>
    }
    if (typeof vectorIcon.value === 'string') {
      return (
        <Box sx={iconContainerStyles}>
          <Typography sx={{ fontSize: `${ICON_DEFAULT_SIZE}px` }}>{vectorIcon.value}</Typography>
        </Box>
      )
    }
  }

  return null
}

const styleSettingToCss = (s: any): CSSProperties => {
  const text = s.settingText
  const border = s.settingBorder
  const style: CSSProperties = {
    backgroundColor: s.background || undefined,
    color: text?.fontColor || undefined,
    fontWeight: text?.isBold ? '700' : undefined,
    fontStyle: text?.isItalic ? 'italic' : undefined,
    textAlign: text?.alignment || undefined,
  }

  if (border) {
    if (border.color && border.color !== 'none') {
      style.borderColor = border.color
      style.borderWidth = 1
      style.borderStyle = 'solid'
    }

    switch (border.type) {
      case BORDER_TYPE.SQUARE:
        style.borderRadius = 0
        break
      case BORDER_TYPE.ROUNDED_SQUARE:
        style.borderRadius = 8
        break
      case BORDER_TYPE.ELLIPSE:
        style.borderRadius = 9999
        break
      default:
        break
    }
  }

  return style
}

const safeExecuteStyleScript = (
  executeJs: ExecutePreviewJs,
  jsSetting: any,
  context: Record<string, unknown>
): string | null => {
  if (!jsSetting) return null

  const script = jsSetting.styleSettingScript?.trim()
  if (!script) return null

  try {
    const { value } = executeJs(script, context)

    return typeof value === 'string' ? value : null
  } catch (err) {
    console.error('DisplayRule script execution error:', err)

    return null
  }
}

const generateStyleFromDisplayRule = (
  executeJs: ExecutePreviewJs,
  displayRule: DisplayRule | null | undefined,
  context: Record<string, unknown>,
  currentValue?: any
): CSSProperties => {
  if (!displayRule) {
    return {}
  }

  const hasData = context && typeof context === 'object' && Object.keys(context).length > 0
  if (!hasData) {
    return {}
  }

  const { jsSetting, displayRuleValues } = displayRule

  if (displayRule.isJs) {
    const code = safeExecuteStyleScript(executeJs, jsSetting, context)
    if (!code) {
      return {}
    }

    const matched = jsSetting?.styleSettings?.find(s => s.code === code)
    if (!matched) {
      return {}
    }

    return styleSettingToCss(matched)
  }

  if (displayRuleValues) {
    const styleSetting = (displayRuleValues as any[]).find(
      rule => rule.objectFieldValue === currentValue
    )?.styleSetting

    if (!styleSetting) {
      return {}
    }

    return styleSettingToCss(styleSetting)
  }

  return {}
}

/**
 * Component for displaying JS code badge with CodeIcon
 */
interface JSCodeBadgeProps {
  tooltip?: string
  badgePosition?: { top?: number; right?: number }
  containerPadding?: string
}

const JSCodeBadge: FC<JSCodeBadgeProps> = ({
  tooltip,
  badgePosition = { top: -3, right: -5 },
  containerPadding = ELEMENT_PADDING,
}) => {
  const badgeContent = (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        cursor: tooltip ? 'pointer' : 'default',
        padding: containerPadding,
      }}
    >
      <CodeIcon
        sx={{
          fontSize: CODE_ICON_FONT_SIZE,
          color: 'inherit',
          verticalAlign: 'middle',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: badgePosition.top,
          right: badgePosition.right,
          minWidth: JS_BADGE_MIN_WIDTH,
          height: JS_BADGE_HEIGHT,
          padding: JS_BADGE_PADDING,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: JS_BADGE_COLOR,
          borderRadius: JS_BADGE_BORDER_RADIUS,
          boxShadow: JS_BADGE_BOX_SHADOW,
        }}
      >
        <Typography
          sx={{
            fontSize: JS_BADGE_FONT_SIZE,
            fontWeight: 600,
            color: 'white',
            lineHeight: 1,
          }}
        >
          JS
        </Typography>
      </Box>
    </Box>
  )

  return tooltip ? (
    <Tooltip
      title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{tooltip}</pre>}
      placement='top'
      arrow
    >
      {badgeContent}
    </Tooltip>
  ) : (
    badgeContent
  )
}

const renderValueContent = (
  element: RowElement,
  display: string,
  tooltip?: string,
  style: CSSProperties = {}
) => {
  const baseStyle: CSSProperties = {
    fontWeight: style.fontWeight ?? (element.title ? 500 : 400),
    ...style,
  }

  // If display is "JS" and we have tooltip, show CodeIcon with badge
  if (display === 'JS' && tooltip) {
    return <JSCodeBadge tooltip={tooltip} />
  }

  if (!tooltip) {
    return <span style={baseStyle}>{display}</span>
  }

  return (
    <Tooltip
      title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{tooltip}</pre>}
      placement='top'
      arrow
    >
      <span style={{ ...baseStyle, cursor: 'pointer', display: 'inline-block' }}>{display}</span>
    </Tooltip>
  )
}

/**
 * Common function to render a field element (used in both Simple and Advanced modes)
 */
interface RenderFieldProps {
  element: RowElement
  record: any
  recordIndex: number
  commandDataByKey: Record<string, any[]>
  commandDataByObject: Record<string, any[]>
  executeJs: ExecutePreviewJs
  list: any[]
  baseContext: BaseScriptContext
  displayRulesMap?: Map<number, DisplayRule>
  styleParameters?: StyleParameters
}

const renderField = ({
  element,
  record,
  recordIndex,
  commandDataByKey,
  commandDataByObject,
  executeJs,
  list,
  baseContext,
  displayRulesMap,
  styleParameters,
}: RenderFieldProps): React.ReactNode => {
  const isJS = element.bindType === 'js'
  const recordHasData = hasData(record)

  // For JS bindType in empty state, show icon without executing code
  if (isJS && !recordHasData) {
    const jsCode = typeof element.value === 'string' ? element.value : ''
    const tooltip = jsCode.trim().length > 0 ? jsCode : undefined

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', bottom: -3 }}>
        <Typography variant='body2' sx={{ fontSize: '11px', lineHeight: 1.4 }}>
          {element.title && <span style={{ color: TEXT_SECONDARY_COLOR }}>{element.title}: </span>}
          <JSCodeBadge
            tooltip={tooltip}
            badgePosition={{ top: -6, right: -10 }}
            containerPadding='0'
          />
        </Typography>
      </Box>
    )
  }

  const {
    display,
    error: elementError,
    tooltip,
    rawValue,
  } = computeElementValue(
    element,
    record,
    recordIndex,
    commandDataByKey,
    commandDataByObject,
    executeJs,
    list,
    baseContext
  )

  // Get full DisplayRule from map
  const fullDisplayRule = element.displayRule?.id
    ? displayRulesMap?.get(element.displayRule.id)
    : null

  const rowContext = { ...commandDataByKey, item: record, list }
  // Generate styles from DisplayRule (only when we have test data)
  const elementStyle: CSSProperties =
    element.useDisplayRule && fullDisplayRule && recordHasData
      ? {
          ...generateStyleFromDisplayRule(executeJs, fullDisplayRule, rowContext, rawValue),
          padding: ELEMENT_PADDING,
        }
      : {}

  // Add style parameters if provided
  const styleParametersStyles = styleParameters ? buildStyleFromParameters(styleParameters) : {}
  const combinedStyles = { ...elementStyle, ...styleParametersStyles }

  // For JS bindType: show icon with badge only in empty state, otherwise show execution result
  const displayContent = isJS ? (
    // Show icon with badge only when there's no data (empty state)
    !recordHasData ? (
      <JSCodeBadge tooltip={tooltip} badgePosition={{ top: -6, right: -10 }} containerPadding='0' />
    ) : // Show execution result with tooltip when we have data
    tooltip ? (
      <Tooltip
        title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{tooltip}</pre>}
        placement='top'
        arrow
      >
        <span
          style={{
            fontWeight: element.title ? 500 : 400,
            ...combinedStyles,
            cursor: 'pointer',
          }}
        >
          {display}
        </span>
      </Tooltip>
    ) : (
      <span style={{ fontWeight: element.title ? 500 : 400, ...combinedStyles }}>{display}</span>
    )
  ) : (
    <span style={{ fontWeight: element.title ? 500 : 400, ...combinedStyles }}>{display}</span>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant='body2' sx={{ fontSize: '11px', lineHeight: 1.4 }}>
        {element.title && <span style={{ color: TEXT_SECONDARY_COLOR }}>{element.title}: </span>}
        {displayContent}
      </Typography>
      {elementError && (
        <Typography variant='caption' color='error.main'>
          {elementError}
        </Typography>
      )}
    </Box>
  )
}

// Render template element (Advanced mode)
const renderTemplateElement = (
  element: TemplateElement,
  record: any,
  commandDataByKey: Record<string, any[]>,
  commandDataByObject: Record<string, any[]>,
  executeJs: ExecutePreviewJs,
  list: any[],
  baseContext: BaseScriptContext,
  displayRulesMap?: Map<number, DisplayRule>
): React.ReactNode => {
  // Check if we have actual data (not empty state)
  // Empty state is when record is empty object {} and list is empty []
  const recordHasData = hasData(record)
  if (element.type === 'view') {
    const viewElement = element as ViewElement
    const style = viewElement.style || {}

    const flexValue =
      style.flex !== undefined
        ? typeof style.flex === 'string'
          ? parseFloat(style.flex)
          : style.flex
        : undefined
    const gapValue =
      style.gap !== undefined
        ? (typeof style.gap === 'string' ? parseFloat(style.gap) : style.gap) + 'px'
        : undefined

    const styleParametersStyles =
      viewElement.useStyleParameters && viewElement.styleParameters
        ? buildStyleFromParameters(viewElement.styleParameters)
        : {}

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: style.flexDirection || 'column',
          flex: flexValue,
          gap: gapValue,
          flexWrap: style.flexWrap === 'no-wrap' ? 'nowrap' : style.flexWrap,
          justifyContent: style.justifyContent,
          alignItems: style.alignItems,
          textAlign: style.textAlign,
          minWidth: flexValue !== undefined && flexValue > 0 ? 0 : undefined, // Add minWidth: 0 when flex is set
          ...styleParametersStyles,
        }}
      >
        {viewElement.children?.map((child, index) =>
          renderTemplateElement(
            child,
            record,
            commandDataByKey,
            commandDataByObject,
            executeJs,
            list,
            baseContext,
            displayRulesMap
          )
        )}
      </Box>
    )
  }

  if (element.type === 'icon') {
    const iconElement = element as IconElement
    const iconConfig = {
      value: iconElement.value,
      isJS: iconElement.isJS,
      size: iconElement.size,
      displayRule: iconElement.displayRule,
    }

    const styleParametersStyles =
      iconElement.useStyleParameters && iconElement.styleParameters
        ? buildStyleFromParameters(iconElement.styleParameters)
        : {}

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...styleParametersStyles,
        }}
      >
        {renderIcon(
          iconConfig,
          record,
          commandDataByKey,
          executeJs,
          list,
          baseContext,
          displayRulesMap
        )}
      </Box>
    )
  }

  if (element.type === 'field') {
    const fieldElement = element as FieldElement

    // Convert FieldElement to RowElement format for computeElementValue
    // This ensures identical processing in Simple and Advanced modes
    const rowElement: RowElement = {
      code: fieldElement.code,
      order: fieldElement.order || 0,
      title: fieldElement.title || '',
      value: fieldElement.value,
      pinnedColumn: fieldElement.pinnedColumn || null,
      objectValue: fieldElement.objectValue || null,
      pathArray: fieldElement.pathArray || null,
      actions: fieldElement.actions || [],
      columnToSort: fieldElement.columnToSort || null,
      objectCode: fieldElement.objectCode,
      valueType: fieldElement.valueType || null,
      fieldType: fieldElement.fieldType || null,
      bindType: fieldElement.bindType || 'field',
      formatDate: fieldElement.formatDate || null,
      editable: fieldElement.editable ?? true,
      enumDescription: fieldElement.enumDescription ?? false,
      visibility: fieldElement.visibility ?? true,
      useLocalParameters: fieldElement.useLocalParameters ?? false,
      useDisplayRule: fieldElement.useDisplayRule ?? false,
      isPk: fieldElement.isPk || null,
      parameters: fieldElement.parameters || [],
      restrictions: fieldElement.restrictions || null,
      valueFormat: fieldElement.valueFormat || null,
      displayRule: fieldElement.displayRule || null,
      preFill: fieldElement.preFill || null,
    }

    // Use common renderField function for consistent behavior
    return renderField({
      element: rowElement,
      record,
      recordIndex: 0,
      commandDataByKey,
      commandDataByObject,
      executeJs,
      list,
      baseContext,
      displayRulesMap,
      styleParameters:
        fieldElement.useStyleParameters && fieldElement.styleParameters
          ? fieldElement.styleParameters
          : undefined,
    })
  }

  return null
}

export const MobileListItemPreview: FC<MobileListItemPreviewProps> = ({
  item,
  data,
  isLoading,
  error,
  onRefresh,
  onDataEdit,
  commandResultsByKey,
  commandResultsByObject,
}) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editedJsonValue, setEditedJsonValue] = useState('')
  const [isJsonValid, setIsJsonValid] = useState(true)
  const [jsonError, setJsonError] = useState<string | null>(null)

  const rows = item.rows || []
  const configMode = item.configMode || 'simple'
  const itemTemplate = item.itemTemplate
  const previewItems = useMemo(() => {
    if (!data?.length) {
      return []
    }

    return data.slice(0, PREVIEW_MAX_ITEMS)
  }, [data])

  const commandDataByKey = commandResultsByKey || {}
  const commandDataByObject = commandResultsByObject || {}

  const executeJs = useExecutePreviewJs()

  const baseScriptContext = useMemo<BaseScriptContext>(
    () => ({
      appData: commandDataByKey,
      appConfig: item,
      userData: {},
    }),
    [commandDataByKey, item]
  )

  // Collect all unique displayRule IDs from elements, icon, and template
  const displayRuleIds = useMemo(() => {
    const ids = new Set<number>()

    // Add icon displayRule if exists (Simple mode)
    if (item.icon?.displayRule?.id) {
      ids.add(item.icon.displayRule.id)
    }

    // Add element displayRules (Simple mode)
    rows.forEach(row => {
      row.elements.forEach(element => {
        if (element.displayRule?.id) {
          ids.add(element.displayRule.id)
        }
      })
    })

    // Add displayRules from template (Advanced mode)
    const templateIds = collectDisplayRuleIdsFromTemplate(itemTemplate)
    templateIds.forEach(id => ids.add(id))

    return Array.from(ids)
  }, [rows, item.icon, itemTemplate])

  // Load display rules for the first 10 unique IDs (React hooks limitation)
  // We need to call hooks unconditionally, so we load up to 10 display rules
  const query1 = useFetchDisplayRuleByIdQuery(
    { id: displayRuleIds[0] },
    { skip: !displayRuleIds[0] }
  )
  const query2 = useFetchDisplayRuleByIdQuery(
    { id: displayRuleIds[1] },
    { skip: !displayRuleIds[1] }
  )
  const query3 = useFetchDisplayRuleByIdQuery(
    { id: displayRuleIds[2] },
    { skip: !displayRuleIds[2] }
  )
  const query4 = useFetchDisplayRuleByIdQuery(
    { id: displayRuleIds[3] },
    { skip: !displayRuleIds[3] }
  )
  const query5 = useFetchDisplayRuleByIdQuery(
    { id: displayRuleIds[4] },
    { skip: !displayRuleIds[4] }
  )
  const query6 = useFetchDisplayRuleByIdQuery(
    { id: displayRuleIds[5] },
    { skip: !displayRuleIds[5] }
  )
  const query7 = useFetchDisplayRuleByIdQuery(
    { id: displayRuleIds[6] },
    { skip: !displayRuleIds[6] }
  )
  const query8 = useFetchDisplayRuleByIdQuery(
    { id: displayRuleIds[7] },
    { skip: !displayRuleIds[7] }
  )
  const query9 = useFetchDisplayRuleByIdQuery(
    { id: displayRuleIds[8] },
    { skip: !displayRuleIds[8] }
  )
  const query10 = useFetchDisplayRuleByIdQuery(
    { id: displayRuleIds[9] },
    { skip: !displayRuleIds[9] }
  )

  // Create a map of displayRule ID to full DisplayRule data
  const displayRulesMap = useMemo(() => {
    const map = new Map<number, DisplayRule>()
    const queries = [
      query1,
      query2,
      query3,
      query4,
      query5,
      query6,
      query7,
      query8,
      query9,
      query10,
    ]

    queries.forEach((query, index) => {
      if (query.data && displayRuleIds[index]) {
        map.set(displayRuleIds[index], query.data)
      }
    })

    return map
  }, [
    query1,
    query2,
    query3,
    query4,
    query5,
    query6,
    query7,
    query8,
    query9,
    query10,
    displayRuleIds,
  ])

  const handleOpenEditDialog = () => {
    setEditedJsonValue(JSON.stringify(data || [], null, 2))
    setJsonError(null)
    setIsJsonValid(true)
    setIsEditDialogOpen(true)
  }

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false)
    setEditedJsonValue('')
    setJsonError(null)
    setIsJsonValid(true)
  }

  const handleJsonChange = (value: string) => {
    setEditedJsonValue(value)
  }

  const handleValidationChange = (isValid: boolean, error: string | null) => {
    setIsJsonValid(isValid)

    // Additional check: ensure it's an array
    if (isValid && editedJsonValue.trim()) {
      try {
        const parsed = JSON.parse(editedJsonValue)
        if (!Array.isArray(parsed)) {
          setIsJsonValid(false)
          setJsonError('Data must be an array')

          return
        }
        setJsonError(null)
      } catch {
        // Will be caught by JsonEditor validation
      }
    } else {
      setJsonError(error)
    }
  }

  const handleSaveEditedData = () => {
    if (!isJsonValid) {
      return
    }

    try {
      const parsed = JSON.parse(editedJsonValue)
      if (!Array.isArray(parsed)) {
        setJsonError('Data must be an array')

        return
      }
      onDataEdit?.(parsed)
      handleCloseEditDialog()
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON')
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onRefresh && (
            <Button size='small' variant='outlined' onClick={onRefresh} disabled={isLoading}>
              {isLoading ? (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} thickness={5} />
                  <span>Loading…</span>
                </Box>
              ) : (
                'Load Test Data'
              )}
            </Button>
          )}
          {onDataEdit && data && data.length > 0 && (
            <Button
              size='small'
              variant='outlined'
              startIcon={<Edit />}
              onClick={handleOpenEditDialog}
              disabled={isLoading}
            >
              Edit Data
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity='warning'>{error}</Alert>}

      <MobilePhoneFrame title='📱 List Item Preview'>
        <Box sx={{ position: 'relative', height: '100%' }}>
          <Box
            sx={{
              padding: '16px',
              height: '100%',
              overflow: 'auto',
              scrollbarWidth: 'thin',
              backgroundColor: BACKGROUND_LIGHT,
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  minHeight: 160,
                }}
              >
                <CircularProgress />
                <Typography variant='body2' color='text.secondary'>
                  Loading Data…
                </Typography>
              </Box>
            ) : previewItems.length ? (
              previewItems.map((record, recordIndex) => (
                <Box key={recordIndex} sx={{ mb: recordIndex < previewItems.length - 1 ? 1.5 : 0 }}>
                  <SingleListItemRenderer
                    item={item}
                    record={record}
                    recordIndex={recordIndex}
                    list={previewItems}
                    commandResultsByKey={commandDataByKey}
                    commandResultsByObject={commandDataByObject}
                    displayRulesMap={displayRulesMap}
                    showChevron={true}
                    executeJs={executeJs}
                    baseScriptContext={baseScriptContext}
                  />
                </Box>
              ))
            ) : (
              <Box>
                <SingleListItemRenderer
                  item={item}
                  record={{}}
                  recordIndex={0}
                  list={[]}
                  commandResultsByKey={commandDataByKey}
                  commandResultsByObject={commandDataByObject}
                  displayRulesMap={displayRulesMap}
                  showChevron={true}
                  executeJs={executeJs}
                  baseScriptContext={baseScriptContext}
                />
              </Box>
            )}
          </Box>

          {/* FAB preview */}
          {item.fab?.icon != null && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: item.fab.backgroundColor || '#508d6b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                color: item.fab.icon.color || '#FFFFFF',
              }}
              title='FAB (preview)'
            >
              {(() => {
                const fabIcon = item.fab!.icon
                const iconType = (fabIcon.type as VectorIconLibrary) || 'MaterialDesignIcons'
                const iconName = fabIcon.value || 'plus'
                const iconSize = fabIcon.size ?? 28
                const iconColor = fabIcon.color || '#FFFFFF'
                const iconForPreview: VectorIcon = {
                  value: iconName,
                  type: iconType,
                  size: iconSize,
                  color: iconColor,
                }
                const rendered = <VectorIconRenderer icon={iconForPreview} />
                if (rendered) return rendered
                return <AddIcon sx={{ fontSize: iconSize, color: iconColor }} />
              })()}
            </Box>
          )}
        </Box>
      </MobilePhoneFrame>

      {/* Dialog for editing preview data */}
      <Dialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle>Edit Preview Data (JSON)</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
          <Box sx={{ flex: 1, p: 2 }}>
            <JsonEditor
              value={editedJsonValue}
              onChange={handleJsonChange}
              onValidationChange={handleValidationChange}
              label='Preview Data Array'
              minHeight={400}
              hideActions
            />
            {jsonError && (
              <Alert severity='error' sx={{ mt: 2 }}>
                {jsonError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            onClick={handleSaveEditedData}
            variant='contained'
            color='primary'
            disabled={!isJsonValid}
          >
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
